// « À régler » : the notification bubble at the bottom left of the floor (on top of the zoom
// buttons) and its panel — what needs a person on this floor (SPEC §12.1): an agent that left a
// decision (« À RÉGLER : … »), an agent blocked by an external cause, a failed run, an engine or a
// connector to fix (admins) and the drafts to approve — then the person's own requests that are
// finished (« Demandes terminées »: a task they launched, told to them only). Every item can be
// opened (its task, the approvals, Administration) and, depending on its kind and the person's
// level, relaunched, answered with an instruction for the agent, marked settled, or marked seen
// (a finished request). In the building view the bubble counts every floor; with nothing but
// finished requests it is blue, not red.
//
// Data: GET /api/notifications (every floor the person sees: items + counts per floor, and among
// them the finished requests: doneCounts, doneTotal), kept in `store.notices`; refreshed when a task event changes what it shows, on reconnection, when the
// office or the connectors change, when the page comes back to the foreground, and every 5 min.
import { h, icon, iconButton, button, emptyState, setText, setAttr, classNames, textarea, monoLabel, clear } from '../dom.js';
import { t, registerStrings } from '../i18n/fr.js';
import { relativeTime } from '../format.js';

registerStrings({
  'notif.button': 'À régler',
  'notif.aria': { one: 'À régler : {n} point sur cet étage', other: 'À régler : {n} points sur cet étage' },
  'notif.ariaNone': 'À régler : rien sur cet étage',
  'notif.ariaBuilding': { one: 'À régler : {n} point dans l’immeuble', other: 'À régler : {n} points dans l’immeuble' },
  'notif.ariaBuildingNone': 'À régler : rien dans l’immeuble',
  'notif.title': 'À RÉGLER',
  'notif.scopeBuilding': 'Tout l’immeuble',
  'notif.panelLabel': 'Ce qui attend une action, et vos demandes terminées',
  'notif.close': 'Fermer',
  'notif.empty.title': 'Rien à régler',
  'notif.empty.text': 'Aucune erreur, aucun blocage ni décision en attente sur cet étage.',
  'notif.empty.textBuilding': 'Aucune erreur, aucun blocage ni décision en attente dans l’immeuble.',
  'notif.unavailable': 'Les notifications ne répondent pas pour le moment.',
  'notif.kind.todo': 'Décision attendue',
  'notif.kind.blocked': 'Agent bloqué',
  'notif.kind.failed': 'Échec',
  'notif.kind.engine': 'Moteur',
  'notif.kind.connector': 'Connecteur',
  'notif.kind.approval': 'À valider',
  'notif.kind.done': 'Demande terminée',
  'notif.kind.studio': 'Pub bloquée',
  'notif.kind.pub': 'Pub à valider',
  'notif.group.studio': 'PUBS BLOQUÉES',
  'notif.group.pub': 'PUBS À VALIDER',
  'notif.action.openAd': 'VOIR LA PUB',
  'notif.action.followUp': 'LA RELANCE',
  'notif.action.kick': 'RELANCER',
  'notif.action.kickHint': 'Le chef du service (ou le directeur) reprend la pub tout de suite.',
  'notif.done.kick': 'Relance envoyée.',
  'notif.done.kickNone': 'Rien à relancer : la pub avance.',
  'notif.group.todo': 'DÉCISIONS ATTENDUES',
  'notif.group.blocked': 'AGENTS BLOQUÉS',
  'notif.group.failed': 'ÉCHECS',
  'notif.group.engine': 'MOTEURS',
  'notif.group.connector': 'CONNECTEURS',
  'notif.group.approval': 'BROUILLONS À VALIDER',
  'notif.group.done': 'VOS DEMANDES TERMINÉES',
  'notif.routine': 'routine',
  'notif.action.open': 'OUVRIR',
  'notif.action.approve': 'VALIDER',
  'notif.action.retry': 'RELANCER',
  'notif.action.reply': 'RÉPONDRE',
  'notif.action.settle': 'RÉGLÉ',
  'notif.action.seen': 'VU',
  'notif.action.fix': 'RÉGLER',
  'notif.action.settleHint': 'Le problème est réglé : il quitte la liste.',
  'notif.action.seenHint': 'Vous avez vu le résultat : la demande quitte votre liste.',
  'notif.action.retryHint': 'Refaire la même demande, une fois la cause réglée.',
  'notif.action.replyHint': 'Envoyer une consigne à l’agent, qui s’en charge.',
  'notif.settleAll': 'Tout marquer comme réglé',
  'notif.seenAll': 'Tout marquer comme vu',
  'notif.reply.label': 'Consigne pour {agent}',
  'notif.reply.placeholder': 'Ex. : fais-la quand même, ou mets-la de côté et passe à la suivante.',
  'notif.reply.send': 'ENVOYER',
  'notif.reply.cancel': 'Annuler',
  'notif.reply.empty': 'Écrivez la consigne.',
  'notif.done.retry': 'Tâche relancée.',
  'notif.done.settle': 'Marqué comme réglé.',
  'notif.done.seen': 'Marqué comme vu.',
  'notif.done.settleAll': { one: '{n} point marqué comme réglé.', other: '{n} points marqués comme réglés.' },
  'notif.done.seenAll': { one: '{n} demande marquée comme vue.', other: '{n} demandes marquées comme vues.' },
  'notif.done.reply': 'Consigne envoyée à {agent}.',
  'notif.theAgent': 'l’agent',
});

/** Display order of the kinds (as the server sorts them). */
export const KIND_ORDER = ['todo', 'studio', 'blocked', 'failed', 'engine', 'connector', 'approval', 'pub', 'done'];
const KIND_ICONS = { todo: 'alert', studio: 'film', blocked: 'alert', failed: 'alert', engine: 'bolt', connector: 'wifi', approval: 'inbox', pub: 'film', done: 'check' };
/** Wait after a relevant task event before reloading (events come in bursts). */
export const REFRESH_DEBOUNCE_MS = 1200;
/** Safety net: reload every 5 min (a problem also expires after 7 days). */
export const POLL_MS = 5 * 60 * 1000;
const MOBILE_QUERY = '(max-width: 767px)';
const REPLY_MAX = 4000;

/** Marks a control of the panel, so that a refresh can give it the focus back. */
function keyed(node, key) {
  node.dataset.fk = key;
  return node;
}

/** The empty notices of a fresh session. */
export function emptyNotices() {
  return { items: [], counts: {}, total: 0, doneCounts: {}, doneTotal: 0, loaded: false, unavailable: false };
}

/** The bubble's badge: '' for none, '9+' above 9 (pure). */
export function badgeText(n) {
  const v = Math.max(0, Math.floor(Number(n) || 0));
  if (!v) return '';
  return v > 9 ? '9+' : String(v);
}

/** Is the building view shown (pure)? */
function isBuilding(ui) {
  return ui?.view === 'building' || !ui?.floorId;
}

/**
 * How many points the bubble shows: the floor's, or every floor's in the building view (pure).
 * @param {{counts?: object, total?: number}} notices
 * @param {{view?: string, floorId?: string|null}} ui
 */
export function countForView(notices, ui) {
  if (!notices) return 0;
  if (isBuilding(ui)) return Math.max(0, Number(notices.total) || 0);
  return Math.max(0, Number(notices.counts?.[ui.floorId]) || 0);
}

/**
 * Among the points of countForView, how many are finished requests (pure).
 * @param {{doneCounts?: object, doneTotal?: number}} notices
 * @param {{view?: string, floorId?: string|null}} ui
 */
export function doneForView(notices, ui) {
  if (!notices) return 0;
  if (isBuilding(ui)) return Math.max(0, Number(notices.doneTotal) || 0);
  return Math.max(0, Number(notices.doneCounts?.[ui.floorId]) || 0);
}

/**
 * Nothing but finished requests in view (the bubble is then blue, not red) (pure).
 * @param {object} notices
 * @param {{view?: string, floorId?: string|null}} ui
 */
export function onlyDone(notices, ui) {
  const total = countForView(notices, ui);
  return total > 0 && doneForView(notices, ui) >= total;
}

/**
 * The items the panel lists: the floor's, or all of them in the building view (pure).
 * @param {{items?: object[]}} notices
 * @param {{view?: string, floorId?: string|null}} ui
 */
export function itemsForView(notices, ui) {
  const items = Array.isArray(notices?.items) ? notices.items : [];
  return isBuilding(ui) ? items : items.filter((it) => it.floorId === ui.floorId);
}

/**
 * The items by kind, in KIND_ORDER (pure).
 * @param {object[]} items
 * @returns {{kind: string, items: object[]}[]}
 */
export function groupItems(items) {
  const groups = new Map();
  for (const it of Array.isArray(items) ? items : []) {
    if (!KIND_ORDER.includes(it?.kind)) continue;
    if (!groups.has(it.kind)) groups.set(it.kind, []);
    groups.get(it.kind).push(it);
  }
  return KIND_ORDER.filter((k) => groups.has(k)).map((kind) => ({ kind, items: groups.get(kind) }));
}

/**
 * What a task summary means for the notifications, as a short signature: a change of it may
 * change the list (pure). Running, queued and plain done tasks share the « quiet » signature.
 * @param {object} summary TaskSummary
 */
export function noticeSignature(summary) {
  if (!summary || summary.idle === true) return 'quiet';
  const { state } = summary;
  if (state === 'awaiting_approval') return 'approval';
  if (state === 'failed') return `failed${summary.settled ? ':settled' : ''}`;
  if (state === 'done' && summary.todo) return `todo${summary.settled ? ':settled' : ''}`;
  if (state === 'done' && summary.blocked) return `blocked${summary.settled ? ':settled' : ''}`;
  // A person's request that just finished: it may be one of mine (the server knows).
  if (state === 'done' && summary.notifyDone === true) return 'done';
  return 'quiet';
}

/**
 * The actions of an item for this person (pure).
 * @param {object} item
 * @param {{canOperate?: boolean, isAdmin?: boolean}} perms
 * @returns {{open: boolean, approve: boolean, retry: boolean, reply: boolean, settle: boolean, fix: boolean}}
 */
export function itemActions(item, { canOperate = false, isAdmin = false } = {}) {
  const kind = item?.kind;
  const task = Boolean(item?.taskId);
  // An alert of the Studio's « contrôle de production »: about an ad (itemId), maybe with the
  // task of its last follow-up (taskId).
  const studio = typeof item?.itemId === 'string' && Boolean(item.itemId);
  return {
    open: task && kind !== 'approval',
    approve: task && kind === 'approval',
    retry: canOperate && task && item.retryable === true && !studio,
    reply: canOperate && task && Boolean(item.agentId && item.deptId) && (kind === 'todo' || kind === 'blocked'),
    // « Vu » on one's own finished request needs no level: it only leaves one's own list.
    settle: (canOperate || kind === 'done') && (task || studio) && item.settleable === true,
    fix: isAdmin && (kind === 'engine' || kind === 'connector') && typeof item.target === 'string',
    openItem: studio,
    kick: canOperate && studio && item.retryable === true,
  };
}

/**
 * The task input of an instruction sent from a notification: what the agent said, then the
 * instruction (pure). The agent gets the context it needs without the previous task.
 * @param {{title?: string, taskId?: string, message?: string}} item
 * @param {string} text
 */
export function replyInput(item, text) {
  const what = String(item?.message ?? '').trim();
  const head = `Suite de ta tâche « ${String(item?.title ?? '').trim() || 'sans titre'} » (${item?.taskId ?? '?'})${what ? ` : ${what}` : ''}`;
  return `${head}\n\nConsigne de la personne : ${String(text ?? '').trim()}`;
}

/** A message as a sentence: trimmed, first letter in capitals (« une décision… » → « Une décision… ») (pure). */
export function sentence(text) {
  const s = String(text ?? '').trim();
  return s ? s.charAt(0).toLocaleUpperCase('fr') + s.slice(1) : '';
}

/** The meta line of an item: agent · service (· floor in the building view) · when (pure). */
export function itemMeta(item, { building = false, now = Date.now() } = {}) {
  const parts = [];
  if (item?.agentName) parts.push(item.agentName);
  if (item?.deptName) parts.push(item.deptName);
  if (building && item?.floorName) parts.push(item.floorName);
  if (item?.source === 'routine') parts.push(t('notif.routine'));
  if (item?.at) parts.push(relativeTime(item.at, now));
  return parts.join(' · ');
}

/**
 * Creates the bubble and its panel.
 * @param {object} ctx app context (docs/UI-CONTRACT.md)
 * @param {{pollMs?: number, debounceMs?: number}} [opts]
 * @returns {{el: HTMLElement, load(): Promise<void>, open(): void, close(): void, destroy(): void}}
 */
export function createNotificationCenter(ctx, { pollMs = POLL_MS, debounceMs = REFRESH_DEBOUNCE_MS } = {}) {
  const { store, api } = ctx;
  const count = h('span', { class: 'notif-count', 'aria-hidden': 'true' });
  const el = h('button', {
    type: 'button', class: 'icon-btn notif-bubble', 'aria-haspopup': 'dialog', 'aria-expanded': 'false',
    title: t('notif.button'), onClick: () => toggle(),
  }, icon('bell', { size: 19 }), count);
  const mobile = globalThis.window?.matchMedia?.(MOBILE_QUERY) || null;
  let layer = null;
  let timer = null;
  let pollTimer = null;
  let loading = null;
  let again = false;
  let destroyed = false;
  let lastShown = 0;
  let failedOnce = false;
  /** taskId → signature of the last summary seen (seeded from the listed items). */
  const signatures = new Map();
  /** Open reply forms (item id → text), kept across re-renders of the panel. */
  const drafts = new Map();
  let openReply = null;

  const notices = () => store.get().notices || emptyNotices();

  async function load() {
    if (destroyed) return;
    if (loading) { again = true; return; }
    loading = (async () => {
      try {
        const data = await api.get('/api/notifications');
        if (destroyed) return;
        const items = Array.isArray(data?.items) ? data.items : [];
        for (const it of items) if (it.taskId) signatures.set(it.taskId, it.kind);
        failedOnce = false;
        const obj = (v) => (v && typeof v === 'object' && !Array.isArray(v) ? v : {});
        store.set({
          notices: {
            items, counts: obj(data?.counts), total: Number(data?.total) || 0,
            doneCounts: obj(data?.doneCounts), doneTotal: Number(data?.doneTotal) || 0, loaded: true, unavailable: false,
          },
        });
      } catch (err) {
        if (destroyed || err?.status === 401) return;
        // An office without the notifications (older server): no bubble at all.
        if (err?.status === 404) store.set({ notices: { ...emptyNotices(), loaded: true, unavailable: true } });
        else failedOnce = true;
        render();
      }
    })();
    try { await loading; } finally {
      loading = null;
      if (again && !destroyed) { again = false; schedule(0); }
    }
  }

  function schedule(delay = debounceMs) {
    if (destroyed) return;
    clearTimeout(timer);
    timer = setTimeout(() => { timer = null; load(); }, Math.max(0, delay));
  }

  function onTask(summary) {
    if (!summary?.id) return;
    const sig = noticeSignature(summary);
    const prev = signatures.get(summary.id);
    signatures.set(summary.id, sig);
    // A new problem or draft, or a listed one that changed (retried, settled, approved…).
    if (prev === undefined ? sig !== 'quiet' : prev !== sig) schedule();
  }

  function onRemoved(data) {
    if (data?.id && signatures.has(data.id)) {
      const listed = signatures.get(data.id) !== 'quiet';
      signatures.delete(data.id);
      if (listed) schedule();
    }
  }

  // --- bubble -------------------------------------------------------------------------------------
  function render() {
    const s = store.get();
    const n = notices();
    el.hidden = Boolean(n.unavailable);
    const value = countForView(n, s.ui);
    setText(count, badgeText(value));
    el.classList.toggle('has-items', value > 0);
    el.classList.toggle('is-info', onlyDone(n, s.ui));
    el.classList.toggle('is-stale', failedOnce);
    const building = isBuilding(s.ui);
    const label = building
      ? (value ? t('notif.ariaBuilding', { n: value }) : t('notif.ariaBuildingNone'))
      : (value ? t('notif.aria', { n: value }) : t('notif.ariaNone'));
    setAttr(el, 'aria-label', label);
    setAttr(el, 'title', label);
    if (value > lastShown && n.loaded) {
      el.classList.remove('is-bump');
      // Restart the animation (prefers-reduced-motion turns it off in CSS).
      void el.offsetWidth;
      el.classList.add('is-bump');
    }
    lastShown = value;
    if (layer?.isOpen()) renderPanel();
  }

  // --- panel ----------------------------------------------------------------------------------------
  const head = h('div', { class: 'notif-head' });
  const body = h('div', { class: 'notif-body' });
  const foot = h('div', { class: 'notif-foot' });
  const panel = h('div', { class: 'notif-panel' }, head, body, foot);

  function floorName(floorId) {
    return store.get().office.floors.find((f) => f.id === floorId)?.name || '';
  }

  function canOperate(item) {
    return Boolean(item.deptId && ctx.can('operate', { floorId: item.floorId, deptId: item.deptId }));
  }

  async function run(btn, fn) {
    if (btn) { btn.disabled = true; btn.classList.add('is-busy'); }
    try {
      await fn();
    } catch (err) {
      ctx.toast?.error?.(err);
    } finally {
      if (btn?.isConnected) { btn.disabled = false; btn.classList.remove('is-busy'); }
    }
  }

  function go(hash) {
    layer?.close('select');
    ctx.navigate(hash);
  }

  async function settle(ids, { floorId = null } = {}) {
    const res = await api.post('/api/notifications/resolve', { ids, ...(floorId ? { floorId } : {}) });
    await load();
    return res;
  }

  function replyForm(item, agentLabel) {
    const area = keyed(textarea({
      class: 'notif-reply-text', rows: 3, maxLength: REPLY_MAX, placeholder: t('notif.reply.placeholder'),
      'aria-label': t('notif.reply.label', { agent: agentLabel }), value: drafts.get(item.id) || '',
    }), `${item.id}:text`);
    area.addEventListener('input', () => drafts.set(item.id, area.value));
    const error = h('p', { class: 'notif-reply-error', role: 'alert', hidden: true });
    const send = keyed(button(t('notif.reply.send'), { variant: 'primary', size: 'sm', icon: 'send' }), `${item.id}:send`);
    const cancel = keyed(button(t('notif.reply.cancel'), {
      variant: 'ghost', size: 'sm', onClick: () => { openReply = null; drafts.delete(item.id); renderPanel({ focus: `${item.id}:reply` }); },
    }), `${item.id}:cancel`);
    send.addEventListener('click', () => run(send, async () => {
      const text = area.value.trim();
      if (!text) { error.hidden = false; setText(error, t('notif.reply.empty')); area.focus(); return; }
      // The decision of the task (POST …/decide): the agent gets its first request and its answer
      // with the instruction, and the task is settled. A task that no longer waits for one (decided
      // meanwhile, a studio item): a new task with what the notification says.
      let summary = null;
      try {
        const res = await api.post(`/api/tasks/${encodeURIComponent(item.taskId)}/decide`, { comment: text });
        summary = res?.followUp ?? null;
      } catch (err) {
        if (err?.status !== 409) throw err;
        summary = await api.post('/api/tasks', {
          floorId: item.floorId, deptId: item.deptId, agentId: item.agentId, input: replyInput(item, text), from: 'user',
        });
      }
      if (summary?.id) store.applyTask?.(summary);
      drafts.delete(item.id);
      openReply = null;
      await settle([item.id]);
      ctx.toast?.success?.(t('notif.done.reply', { agent: agentLabel }));
    }));
    area.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); send.click(); }
    });
    return h('div', { class: 'notif-reply' },
      h('label', { class: 'notif-reply-label mono-label' }, t('notif.reply.label', { agent: agentLabel })),
      area, error, h('div', { class: 'notif-reply-actions' }, cancel, send));
  }

  function itemNode(item, { building }) {
    const acts = itemActions(item, { canOperate: canOperate(item), isAdmin: store.get().me?.role === 'admin' });
    const agentLabel = item.agentName || t('notif.theAgent');
    const buttons = [];
    const add = (action, node) => buttons.push(keyed(node, `${item.id}:${action}`));
    if (acts.openItem) add('openItem', button(t('notif.action.openAd'), { variant: item.kind === 'pub' ? 'amber' : 'ghost', size: 'sm', onClick: () => go(`#/studio/item/${encodeURIComponent(item.itemId)}`) }));
    if (acts.open) add('open', button(t(acts.openItem ? 'notif.action.followUp' : 'notif.action.open'), { variant: 'ghost', size: 'sm', onClick: () => go(`#/task/${encodeURIComponent(item.taskId)}`) }));
    if (acts.kick) {
      const b = button(t('notif.action.kick'), { variant: 'secondary', size: 'sm', icon: 'refresh', title: t('notif.action.kickHint') });
      b.addEventListener('click', () => run(b, async () => {
        const out = await api.post(`/api/studio/watch/${encodeURIComponent(item.itemId)}/kick`, {});
        await load();
        ctx.toast?.success?.(t(out?.followUp ? 'notif.done.kick' : 'notif.done.kickNone'));
      }));
      add('kick', b);
    }
    if (acts.approve) add('approve', button(t('notif.action.approve'), { variant: 'amber', size: 'sm', onClick: () => go('#/approvals') }));
    if (acts.fix) add('fix', button(t('notif.action.fix'), { variant: 'secondary', size: 'sm', icon: 'settings', onClick: () => go(`#/admin/${encodeURIComponent(item.target)}`) }));
    if (acts.retry) {
      const b = button(t('notif.action.retry'), { variant: 'secondary', size: 'sm', icon: 'refresh', title: t('notif.action.retryHint') });
      b.addEventListener('click', () => run(b, async () => {
        const summary = await api.post(`/api/tasks/${encodeURIComponent(item.taskId)}/retry`, {});
        if (summary?.id) store.applyTask?.(summary);
        await load();
        ctx.toast?.success?.(t('notif.done.retry'));
      }));
      add('retry', b);
    }
    if (acts.reply) {
      add('reply', button(t('notif.action.reply'), {
        variant: openReply === item.id ? 'primary' : 'secondary', size: 'sm', icon: 'send', title: t('notif.action.replyHint'),
        onClick: () => {
          openReply = openReply === item.id ? null : item.id;
          renderPanel({ focus: openReply ? `${item.id}:text` : `${item.id}:reply` });
        },
      }));
    }
    if (acts.settle) {
      const seen = item.kind === 'done';
      const b = button(t(seen ? 'notif.action.seen' : 'notif.action.settle'), {
        variant: 'secondary', size: 'sm', icon: 'check', title: t(seen ? 'notif.action.seenHint' : 'notif.action.settleHint'),
      });
      b.addEventListener('click', () => run(b, async () => {
        await settle([item.id]);
        ctx.toast?.success?.(t(seen ? 'notif.done.seen' : 'notif.done.settle'));
      }));
      add('settle', b);
    }
    const kindLabel = t(`notif.kind.${item.kind}`);
    return h('article', { class: classNames(['notif-item', `is-${item.kind}`]), dataset: { id: item.id } },
      h('span', { class: 'notif-item-icon', 'aria-hidden': 'true' }, icon(KIND_ICONS[item.kind] || 'alert', { size: 16 })),
      h('div', { class: 'notif-item-main' },
        h('p', { class: 'notif-item-title' }, h('span', { class: 'sr-only' }, `${kindLabel} : `), item.title),
        h('p', { class: 'notif-item-meta' }, itemMeta(item, { building, now: Date.now() + (store.get().serverOffset || 0) })),
        item.message ? h('p', { class: 'notif-item-msg' }, sentence(item.message)) : null,
        buttons.length ? h('div', { class: 'notif-item-actions' }, ...buttons) : null,
        acts.reply && openReply === item.id ? replyForm(item, agentLabel) : null));
  }

  /**
   * (Re)builds the panel. A refresh keeps the keyboard focus where it was (the same control, and
   * the caret of a reply being typed), or on the panel when that control is gone; `focus` moves it
   * to the control with that key instead.
   * @param {{focus?: string|null}} [opts]
   */
  function renderPanel({ focus = null } = {}) {
    const active = globalThis.document?.activeElement || null;
    const within = Boolean(active && active !== panel && panel.contains(active));
    const prevKey = within ? active.dataset?.fk || null : null;
    const caret = within && typeof active.selectionStart === 'number' ? [active.selectionStart, active.selectionEnd] : null;
    const s = store.get();
    const n = notices();
    const building = isBuilding(s.ui);
    const items = itemsForView(n, s.ui);
    const scope = building ? t('notif.scopeBuilding') : floorName(s.ui.floorId);
    clear(head);
    head.append(
      h('div', { class: 'notif-head-text' }, monoLabel(t('notif.title')), scope ? h('p', { class: 'notif-scope' }, scope) : null),
      items.length ? h('span', {
        class: classNames(['notif-head-count', items.every((it) => it.kind === 'done') && 'is-info']), 'aria-hidden': 'true',
      }, String(items.length)) : null,
      keyed(iconButton('close', t('notif.close'), { variant: 'ghost', onClick: () => layer?.close('button') }), 'close'));
    clear(body);
    if (!items.length) {
      body.append(failedOnce && !n.loaded
        ? emptyState({ icon: 'wifi', title: t('notif.empty.title'), text: t('notif.unavailable') })
        : emptyState({ icon: 'check', title: t('notif.empty.title'), text: building ? t('notif.empty.textBuilding') : t('notif.empty.text') }));
    } else {
      for (const group of groupItems(items)) {
        body.append(h('section', { class: classNames(['notif-group', `is-${group.kind}`]) },
          h('h3', { class: 'notif-group-title mono-label' }, t(`notif.group.${group.kind}`), h('span', { class: 'notif-group-count' }, String(group.items.length))),
          ...group.items.map((it) => itemNode(it, { building }))));
      }
    }
    clear(foot);
    const settleable = items.filter((it) => it.settleable && (it.kind === 'done' || canOperate(it)));
    foot.hidden = building || settleable.length < 2;
    if (!foot.hidden) {
      // Only finished requests: « vu » ; otherwise « réglé » (the finished requests go with them).
      const seenOnly = settleable.every((it) => it.kind === 'done');
      const b = keyed(button(t(seenOnly ? 'notif.seenAll' : 'notif.settleAll'), { variant: 'link', size: 'sm', icon: 'check' }), 'settle-all');
      b.addEventListener('click', () => run(b, async () => {
        const res = await api.post('/api/notifications/resolve', { floorId: s.ui.floorId, all: true });
        await load();
        ctx.toast?.success?.(t(seenOnly ? 'notif.done.seenAll' : 'notif.done.settleAll', { n: Number(res?.resolved) || 0 }));
      }));
      foot.append(b);
    }
    reposition();
    const key = focus || prevKey;
    if (!key && !within) return;
    const target = key ? [...panel.querySelectorAll('[data-fk]')].find((node) => node.dataset.fk === key) || null : null;
    if (target) {
      target.focus({ preventScroll: true });
      if (!focus && caret) {
        try { target.setSelectionRange?.(caret[0], caret[1]); } catch { /* not a text field */ }
      }
    } else if (within && layer?.isOpen()) {
      // The focused control went with its item (settled, retried…): stay in the panel.
      (layer.panel || panel).focus?.({ preventScroll: true });
    }
  }

  /** Keeps the panel above the bubble while its content changes (the overlay places it once). */
  function reposition() {
    const p = layer?.panel;
    if (!p || !layer.isOpen() || typeof el.getBoundingClientRect !== 'function') return;
    const r = el.getBoundingClientRect();
    const vw = globalThis.innerWidth || 0;
    const vh = globalThis.innerHeight || 0;
    const pw = p.offsetWidth;
    const ph = p.offsetHeight;
    if (!pw || !ph || !vw || !vh) return;
    let top = r.top - ph - 10;
    if (top < 8) top = Math.max(8, Math.min(r.bottom + 10, vh - ph - 8));
    let left = mobile?.matches ? r.right - pw : r.left;
    left = Math.max(8, Math.min(left, vw - pw - 8));
    p.style.setProperty('top', `${Math.round(top)}px`);
    p.style.setProperty('left', `${Math.round(left)}px`);
  }

  function open() {
    if (layer?.isOpen()) return;
    openReply = null;
    renderPanel();
    layer = ctx.overlay.popover(el, panel, {
      placement: mobile?.matches ? 'top-end' : 'top-start', className: 'notif-pop', label: t('notif.panelLabel'), initialFocus: 'panel',
    });
    setAttr(el, 'aria-expanded', 'true');
    layer.onClose(() => { setAttr(el, 'aria-expanded', 'false'); layer = null; openReply = null; });
    reposition();
    load();
  }

  function close() {
    layer?.close('api');
  }

  function toggle() {
    if (layer?.isOpen()) close();
    else open();
  }

  // --- wiring ---------------------------------------------------------------------------------------
  const onVisible = () => { if (globalThis.document?.visibilityState === 'visible') schedule(0); };
  globalThis.document?.addEventListener?.('visibilitychange', onVisible);
  const offs = [
    store.on('notices', render),
    store.on('ui', render),
    store.on('me', () => schedule(0)),
    ctx.events.on('task', onTask),
    ctx.events.on('task.removed', onRemoved),
    ctx.events.on('hello', () => schedule(0)),
    ctx.events.on('office', () => schedule()),
    // The Studio's « contrôle de production » changed its alerts.
    ctx.events.on('notices', () => schedule()),
    ctx.events.on('connectors', () => schedule()),
    () => globalThis.document?.removeEventListener?.('visibilitychange', onVisible),
  ];
  pollTimer = setInterval(() => load(), Math.max(10_000, pollMs));
  render();
  load();

  return {
    el,
    load,
    open,
    close,
    destroy() {
      destroyed = true;
      clearTimeout(timer);
      clearInterval(pollTimer);
      layer?.close('api');
      for (const off of offs) {
        try { off?.(); } catch { /* already gone */ }
      }
    },
  };
}
